import { Skeleton, Stack } from "@mui/material";
import React from "react";

import { ACCOUNT_TYPE_OPTIONS, DIALOG_HELPER_TEXTS } from "./AccountEditorConsts";
import {
  validateAccountName,
  validateAccountId,
  validateSalesforceAccountId,
} from "./AccountEditor.utils";
import HyroField from "@hyro-components/fields/HyroField";
import { FIELD_TYPES } from "@hyro-components/fields/utils";

const AccountEditorFields = ({
  data,
  onChange,
  type,
  timezoneOptions,
  isLoadingSftpSettings,
  invalidIpIndexes,
  isReadOnly,
}) => (
  <Stack gap={3}>
    <HyroField
      onChange={(value) =>
        onChange({ target: { value, name: "name" } }, validateAccountName)
      }
      value={data?.name.value}
      data-testid="create-account-input-name"
      name="name"
      label="Account name"
      error={!!data?.name.errorText}
      helperText={data?.name.errorText || " "}
      isReadOnly={isReadOnly}
      showTopSection
      fullWidth
    />

    {type === "edit" && (
      <>
        <HyroField
          onChange={(value) =>
            onChange(
              { target: { value, name: "accountId" } },
              validateAccountId
            )
          }
          value={data?.accountId.value}
          data-testid="create-account-input-id"
          name="accountId"
          label="Account Id"
          error={!!data?.accountId.errorText}
          helperText={data?.accountId.errorText || " "}
          isReadOnly
          showTopSection
          fullWidth
        />
        <HyroField
          onChange={(value) =>
            onChange(
              { target: { value, name: "salesforceAccountId" } },
              validateSalesforceAccountId
            )
          }
          value={data?.salesforceAccountId?.value || ""}
          data-testid="account-salesforce-id"
          name="salesforceAccountId"
          label="Salesforce Account ID"
          error={!!data?.salesforceAccountId?.errorText}
          helperText={data?.salesforceAccountId?.errorText || " "}
          isReadOnly={isReadOnly}
          showTopSection
          fullWidth
        />
        <HyroField
          fieldType={FIELD_TYPES.CHOOSE_ONE}
          onChange={(value) => onChange({ target: { value, name: "accountType" } })}
          value={data?.accountType?.value}
          data-testid='account-type-select'
          name="accountType"
          label="Account type"
          options={ACCOUNT_TYPE_OPTIONS}
          isReadOnly={isReadOnly}
          showTopSection
          fullWidth
        />
        <HyroField
          fieldType={FIELD_TYPES.CHOOSE_ONE}
          onChange={(value) =>
            onChange({ target: { value, name: "timezone" } })
          }
          value={data?.timezone?.value}
          data-testid="account-timezone-select"
          name="timezone"
          label="Time zone"
          description="Default for all agents in this account. Time zones can be overridden per agent in its settings."
          options={timezoneOptions}
          isReadOnly={isReadOnly}
          showTopSection
          fullWidth
        />
        {isLoadingSftpSettings ? (
          <SftpSettingsSkeleton />
        ) : (
          <>
            <HyroField
              isReadOnly
              value={data?.sftpUrl?.value}
              data-testid="account-sftp-url"
              name="sftpUrl"
              label="SFTP folder path"
              tooltip="Path to hyro cloud storage"
              isFieldReadOnlyFromSpec
              isMultiLine
              showTopSection
              fullWidth
            />
            <HyroField
              fieldType={FIELD_TYPES.LIST}
              onChange={(value) =>
                onChange({ target: { value, name: "sftpIpList" } })
              }
              value={data?.sftpIpList?.value || []}
              invalidIndexes={invalidIpIndexes}
              error={
                invalidIpIndexes?.length > 0
                  ? DIALOG_HELPER_TEXTS.ipAddress
                  : ""
              }
              data-testid="account-sftp-ip-list"
              name="sftpIpList"
              label="SFTP IP list"
              isReadOnly={isReadOnly}
              showTopSection
              fullWidth
            />
            <HyroField
              onChange={(value) =>
                onChange({ target: { value, name: "publicKey" } })
              }
              value={data?.publicKey?.value}
              data-testid="account-ssh-public-key"
              name="publicKey"
              label="SSH public key"
              isReadOnly={isReadOnly}
              showTopSection
              fullWidth
            />
          </>
        )}
      </>
    )}
  </Stack>
);

const SftpSettingsSkeleton = () => (
  <Stack direction="column" gap={2}>
    <Stack direction="column" gap={1}>
      <Skeleton variant="text" sx={{ fontSize: 16, width: 100 }} />
      <Skeleton variant="rounded" height={56} />
    </Stack>
    <Stack direction="column" gap={1}>
      <Skeleton variant="text" sx={{ fontSize: 16, width: 120 }} />
      <Skeleton variant="rounded" height={56} />
    </Stack>
    <Stack direction="column" gap={1}>
      <Skeleton variant="text" sx={{ fontSize: 16, width: 140 }} />
      <Skeleton variant="rounded" height={56} />
    </Stack>
  </Stack>
);

export default AccountEditorFields;
